import { motion } from "framer-motion";
import { Sparkles, Zap, Target, Shield, Clock, Users } from "lucide-react";

const WhyUsSection = () => {
  const features = [
    {
      icon: Sparkles,
      title: "IA Integrada",
      description:
        "Automatización inteligente y análisis predictivo que multiplican tus resultados.",
      gradient: "from-purple-500 to-pink-500",
    },
    {
      icon: Target,
      title: "Enfoque en Conversión",
      description:
        "Cada pixel diseñado para convertir visitantes en clientes de alto valor.",
      gradient: "from-blue-500 to-cyan-500",
    },
    {
      icon: Zap,
      title: "Velocidad Premium",
      description:
        "Desarrollo ágil sin sacrificar calidad. Tu proyecto listo en tiempo récord.",
      gradient: "from-amber-500 to-orange-500",
    },
    {
      icon: Shield,
      title: "Escalabilidad Total",
      description:
        "Arquitectura robusta preparada para crecer con tu negocio sin límites.",
      gradient: "from-emerald-500 to-teal-500",
    },
    {
      icon: Clock,
      title: "Soporte 24/7",
      description:
        "Equipo dedicado disponible cuando lo necesites. Tu éxito es nuestra prioridad.",
      gradient: "from-rose-500 to-red-500",
    },
    {
      icon: Users,
      title: "Equipo Élite",
      description:
        "Expertos con +10 años de experiencia en proyectos de alto impacto.",
      gradient: "from-indigo-500 to-violet-500",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section
      id="nosotros"
      className="py-24 md:py-32 bg-[#030303] relative overflow-hidden"
      data-testid="why-us-section"
    >
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#2979FF]/5 rounded-full blur-[150px]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00E5FF] mb-4 block">
            Por qué elegirnos
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4">
            La diferencia que{" "}
            <span className="gradient-text">impulsa resultados</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            No solo construimos productos digitales, creamos ventajas competitivas
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="group p-8 rounded-3xl bg-[#0A0A0A] border border-white/5 hover:border-white/10 transition-all duration-500"
                data-testid={`why-us-feature-${index}`}
              >
                {/* Icon */}
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.gradient} bg-opacity-20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}
                  style={{
                    background: `linear-gradient(135deg, ${feature.gradient
                      .replace("from-", "")
                      .replace("to-", ", ")
                      .replace("-500", "")})`,
                    opacity: 0.2,
                  }}
                >
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center`}
                    style={{
                      background: `linear-gradient(135deg, var(--tw-gradient-from), var(--tw-gradient-to))`,
                    }}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-[#00E5FF] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-20 p-8 md:p-12 rounded-3xl bg-gradient-to-r from-[#2979FF]/10 to-[#00E5FF]/10 border border-white/5"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "150+", label: "Proyectos" },
              { value: "50+", label: "Clientes Activos" },
              { value: "8+", label: "Años de Experiencia" },
              { value: "5x", label: "ROI Promedio" },
            ].map((stat, index) => (
              <div key={index}>
                <div className="text-3xl md:text-4xl font-bold gradient-text mb-2">
                  {stat.value}
                </div>
                <div className="text-zinc-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyUsSection;
